import { ReactElement, useEffect } from "react";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { styled } from "@twilio-paste/core/styling-library";
import {
  useLexicalComposerContext,
  CLEAR_EDITOR_COMMAND,
  KEY_ENTER_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from "@twilio-paste/core/lexical-library";
import { SendIcon } from "@twilio-paste/icons/esm/SendIcon";

import { types } from "../definitions";
import { dataTestIds } from "@twiliointernal/org/twilio/e2-commons/shared/definitions";

// Having the SendIcon button within ChatComposer seems to add
// an `inset` CSS attribute that misaligns the icon on loading.
// This should address that issue
const SendButtonContainer = styled(Box)<{ loading: boolean }>`
  ${({ loading }): string | boolean =>
    loading &&
    `
  top: 0.5rem;
  right: 1rem;
`}
`;

// A chat centric CTA that will clear the associated editor on click,
// and can be customized to respond to key enter events, customary of
// chat use-cases
export const ChatSendButtonPlugin = ({
  isChatActive,
  loading,
  shouldClearInput,
  disabled = false,
  handleOnClick,
  handleOnEnter,
}: types.ChatSendButtonPluginProps): ReactElement => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (handleOnEnter) {
      return editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event: KeyboardEvent | null) => {
          const isValidEnterKeyEvent = event !== null && !event.shiftKey;
          if (isValidEnterKeyEvent && !loading && !disabled) {
            event.preventDefault();
            handleOnEnter();
          }

          return true;
        },
        COMMAND_PRIORITY_HIGH
      );
    }
  }, [isChatActive, loading, disabled]);

  /* Mitigates race condition where the lexical editor clears input before handleOnEnter
    can trigger (React Form state is cleared) - resulting in a validation error on submit.
    shouldClearInput is a boolean that needs to be set/reset externally. We're using React Form's
    form state to trigger this */
  useEffect(() => {
    if (shouldClearInput) {
      editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
    }
  }, [shouldClearInput]);

  const handleSendMessage = (): void => {
    if (handleOnClick) {
      handleOnClick();
    }
  };

  return (
    <SendButtonContainer
      loading={loading}
      position="absolute"
      top="space30"
      right="space30"
    >
      <Button
        loading={loading}
        variant="primary_icon"
        size="reset"
        onClick={handleSendMessage}
        type="submit"
        disabled={!loading && disabled}
        data-testid={dataTestIds.wiseOwlChat.submitButton}
      >
        <SendIcon decorative={false} title="Send message" />
      </Button>
    </SendButtonContainer>
  );
};
